import React, { useState, useEffect, useRef } from "react";
import { Paperclip, Send } from "lucide-react";
import { Message, QuotaSnapshot } from "@/types/chat";
import {
    MAX_TEXT_WORDS,
    countWords,
    validateTextMessage,
    validatePdfFile,
} from "@/utils/chatValidation";
import { useNavigate } from "react-router-dom";

interface ChatInputAreaProps {
    onSendMessage: (message: string) => Promise<void> | void;
    onUploadPdf: (file: File) => Promise<void> | void;
    onInputError?: (message: string) => void;
    placeholder?: string;
    disabled?: boolean;
    messages?: Message[];
    quota?: QuotaSnapshot | null;
}

function clampToMaxWords(text: string, maxWords: number): string {
    const words = text.trim().split(/\s+/).filter(Boolean);
    return words.slice(0, maxWords).join(" ");
}

function getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }
    return "Προέκυψε σφάλμα κατά την επεξεργασία.";
}


function formatResetTime(resetAt?: string) {
    if (!resetAt) return null;

    const date = new Date(resetAt);
    if (isNaN(date.getTime())) return resetAt;

    return date.toLocaleString("el-GR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function ChatInputArea({
    onSendMessage,
    onUploadPdf,
    onInputError,
    placeholder = "Γράψε το κείμενό σου",
    disabled = false,
    messages = [],
    quota = null,
}: ChatInputAreaProps) {
    const [message, setMessage] = useState("");
    const [localError, setLocalError] = useState("");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const userMessages = messages.filter((msg) => msg.role === "user");
    const wordCount = countWords(message);

    const totalLimit = quota?.totalLimit ?? 10;
    const quotaFull =
        quota != null && quota.totalUsed >= totalLimit;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [userMessages.length]);

    const showError = (msg: string) => {
        setLocalError(msg);
        onInputError?.(msg);
    };

    const clearError = () => {
        setLocalError("");
    };

    const submitMessage = async () => {
        if (disabled || quotaFull) return;

        const trimmed = message.trim();
        const validationError = validateTextMessage(trimmed);

        if (validationError) {
            showError(validationError);
            return;
        }

        try {
            clearError();
            await onSendMessage(trimmed);
            setMessage("");
        } catch (error) {
            showError(getErrorMessage(error));
        }
    };

    const handleSubmit = (e?: React.SyntheticEvent) => {
        e?.preventDefault();
    
        const consent = localStorage.getItem("myapp_consent_v1");
    
        if (consent === "declined") {
            navigate("/consent-page", {
                state: { reason: "declined" },
                replace: true,
            });
            return;
        }
    
        void submitMessage();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void submitMessage();
        }
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (disabled || quotaFull) return;

        const words = countWords(value);

        if (words > MAX_TEXT_WORDS) {
            const clamped = clampToMaxWords(value, MAX_TEXT_WORDS);
            setMessage(clamped);
            showError(`Το κείμενο δεν μπορεί να ξεπερνά τις ${MAX_TEXT_WORDS} λέξεις.`);
            return;
        }

        setMessage(value);
        if (localError) clearError();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || disabled || quotaFull) return;

        try {
            const validationError = await validatePdfFile(file);
            if (validationError) {
                showError(validationError);
                e.target.value = "";
                return;
            }

            clearError();
            await onUploadPdf(file);
        } catch (error) {
            showError(getErrorMessage(error));
        } finally {
            e.target.value = "";
        }
    };

    const quotaSummary = quota
        ? `Αιτήματα: ${quota.totalUsed}/${totalLimit} (κείμενο: ${quota.messagesUsed}, PDF: ${quota.filesUsed})`
        : "";

    const isQuotaExceeded =
        quota && quota.totalUsed >= (quota.totalLimit ?? 10);

    return (
        <div className="flex-1 flex flex-col">
            <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-black">Κείμενο ή Έγγραφο</h3>
                <p className="text-sm font-light">
                    Εισάγετε το κείμενο ή ανεβάστε το αρχείο ώστε το Fairlegal να εντοπίσει και να εξαλείψει την μεροληψία λόγω φύλου σε νομικά και διοικητικά κείμενα του δημόσιου και ιδιωτικού τομέα.
                </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                    {userMessages.length > 0 ? (
                        userMessages.map((msg) => (
                            <div
                                key={msg.id}
                                className="bg-gray-100 border border-gray-200 rounded-lg p-4"
                            >
                                <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                                            <svg
                                                className="w-5 h-5 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <div className="text-sm text-gray-800 leading-relaxed">
                                            {msg.content}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">
                                            {msg.timestamp.toLocaleTimeString("el-GR", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-8 h-full py-42">
                            {/* <div className="max-w-md mx-auto">
                                <div className="text-gray-400 mb-4">
                                    <svg
                                        className="w-16 h-16 mx-auto"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1}
                                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                    Δεν υπάρχουν ερωτήσεις
                                </h3>
                                <p className="text-gray-600">
                                    Οι ερωτήσεις σας θα εμφανίζονται εδώ
                                </p> */}
                            {/* </div> */}
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            <div className="flex-col p-6 border-t border-gray-200">
                {quota && isQuotaExceeded && (
                    <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                        Έχετε φτάσει το όριο αιτημάτων.{" "}
                        {quota.resetAt && (
                            <span>
                                Διαθέσιμο ξανά:{" "}
                                <strong>{formatResetTime(quota.resetAt)}</strong>
                            </span>
                        )}
                    </div>
                )}
                <div className="flex items-center px-4 border rounded-lg gap-4 bg-white">
                    <div className="text-center transition-colors">
                        <label
                            aria-label="file-upload"
                            htmlFor="file-upload"
                            className={
                                disabled || quotaFull
                                    ? "cursor-not-allowed opacity-50"
                                    : "cursor-pointer"
                            }
                        >
                            <Paperclip className="mx-auto h-5 w-5 text-gray-500" />
                            <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                accept="application/pdf,.pdf"
                                className="sr-only"
                                disabled={disabled || quotaFull}
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>

                    <div className="flex-1">
                        <input
                            value={message}
                            name="message"
                            aria-label="message-input"
                            onChange={handleMessageChange}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                quotaFull
                                    ? "Έχετε φτάσει το όριο των 10 αιτημάτων / 24 ώρες."
                                    : disabled
                                      ? "Παρακαλώ περιμένετε..."
                                      : placeholder
                            }
                            disabled={disabled || quotaFull}
                            className={`flex items-end w-full h-full resize-none focus:outline-none ${
                                disabled ? "cursor-not-allowed" : ""
                            }`}
                        />
                    </div>

                    <div>
                        <button
                            aria-label="send-button"
                            onClick={handleSubmit}
                            disabled={
                                !message.trim() ||
                                disabled ||
                                quotaFull ||
                                wordCount > MAX_TEXT_WORDS
                            }
                            className="w-full disabled:cursor-not-allowed cursor-pointer py-3 px-4 transition-colors duration-200 flex items-center justify-center space-x-2"
                        >
                            <Send className="w-5 h-5 text-black" />
                        </button>
                    </div>
                </div>

                <div className="mt-2 flex flex-col gap-1 text-xs">
                    <span
                        className={
                            wordCount > MAX_TEXT_WORDS ? "text-red-500" : "text-gray-500"
                        }
                    >
                        {wordCount}/{MAX_TEXT_WORDS} λέξεις
                    </span>

                    {quota && <span className="text-gray-500">{quotaSummary}</span>}

                    {quotaFull && (
                        <span className="text-amber-700">
                            Δεν μπορείτε να στείλετε άλλο μήνυμα ή αρχείο μέχρι να επαναφερθεί το όριο (24
                            ώρες από το πρώτο αίτημα στο παράθυρο).
                        </span>
                    )}

                    {localError && <span className="text-red-500">{localError}</span>}
                </div>
            </div>
        </div>
    );
}